import { cn } from '@/lib/utils';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import * as React from 'react';

interface CheckboxProps
    extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
    variant?: 'default' | 'fluid';
    // The check glyph is a fixed size baked into this component, so a
    // caller resizing the box via `className` alone leaves a mismatched
    // tick floating inside it — pass a matching size here too.
    iconClassName?: string;
}

const DEFAULT_CLASSNAME =
    'peer data-[state=checked]:bg-foreground disabled:opacity-50 border border- data-[state=checked]:border-foreground rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring ring-offset-background focus-visible:ring-offset-2 w-4 h-4 data-[state=checked]:text-background disabled:cursor-not-allowed shrink-0';

const FLUID_CLASSNAME =
    'peer size-4 shrink-0 rounded-[0.3125rem] border-[0.09375rem] border-muted-foreground/50 bg-transparent text-foreground outline-none transition-colors duration-80 hover:border-muted-foreground data-[state=checked]:border-transparent focus-visible:ring-1 focus-visible:ring-focus-ring disabled:opacity-50 disabled:cursor-not-allowed';

const Checkbox = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    CheckboxProps
>(({ className, iconClassName, variant = 'default', ...props }, ref) => (
    <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
            variant === 'fluid' ? FLUID_CLASSNAME : DEFAULT_CLASSNAME,
            className
        )}
        {...props}
    >
        <CheckboxPrimitive.Indicator
            className={cn('flex justify-center items-center text-current')}
        >
            {variant === 'fluid' ? (
                <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={cn('size-3.5', iconClassName)}
                >
                    <motion.path
                        d="M3.5 8.5l3 3 6-7"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.08, ease: 'easeOut' }}
                    />
                </svg>
            ) : (
                <Check className={cn('w-4 h-4', iconClassName)} />
            )}
        </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
